import { Statuses } from '@geee-be/core';
import type { Patrol } from '@local/common-lib';
import { useMediaQuery, useTheme } from '@mui/material';
import type { LocaleOptions } from 'luxon';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useSetTitle } from '../../hooks/use-title.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { ErrorPage } from '../error/index.js';
import { NotFoundPage } from '../not-found/index.js';

const patrolName = (
  patrol: Patrol.Type,
  titleFormat: Intl.DateTimeFormatOptions,
): string =>
  (patrol.name ? `${patrol.name} - ` : '') +
  DateTime.fromJSDate(patrol.when).toLocaleString(titleFormat);

const ExistingPatrolPageInner: FC<{
  id: string;
  view: FC<ExistingEntityViewProps<Patrol.Type>>;
}> = ({ id, view: View }) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [response, refresh, fetchNow] = useAxios<Patrol.Type>(
    `/api/v1/patrol/${encodeURIComponent(id)}`,
  );
  const titleFormat: LocaleOptions & Intl.DateTimeFormatOptions = smallScreen
    ? { day: 'numeric', month: 'short', weekday: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'long', weekday: 'long', year: 'numeric' };
  useSetTitle(
    response.loading
      ? 'Loading...'
      : response.error
        ? 'Error'
        : patrolName(response.data, titleFormat),
  );

  if (response.loading) return <MiddleSpinner />;
  if (response.error?.response?.status === Statuses.NOT_FOUND) {
    return <NotFoundPage />;
  }
  if (response.error) return <ErrorPage error={response.error} />;

  return (
    <View data={response.data} fetchNow={fetchNow} id={id} refresh={refresh} />
  );
};

export const ExistingPatrolPage: FC<{
  view: FC<ExistingEntityViewProps<Patrol.Type>>;
}> = ({ view }) => {
  const { id } = useParams<{ id: string }>();
  if (!id) return <ErrorPage error={new Error('Invalid URL')} />;

  return <ExistingPatrolPageInner id={id} view={view} />;
};
